import * as tracker from '@leroy-merlin-br/backpack-analytics';
import { useMutation } from '@tanstack/react-query';

import { Services } from '@/infra/services';
import { type Response as AddServicesToCartResponse } from '@/infra/services/checkout/cart/add-services-to-cart';
import { type AddedService } from '@/infra/services/checkout/model';
import { useUserResources } from '@/presenters/hooks/user';
import { useServiceState } from '@/presenters/store/catalog/service';

interface UseAddServicesToCart {
  onError?: () => void;
  onSuccess?: () => void;
}

const useAddServicesToCart = (props: Readonly<UseAddServicesToCart>) => {
  const { updateCartItems } = useUserResources();
  const itemId = useServiceState((state) => state.service._id);

  const { onError, onSuccess } = props;

  const pushAddToCart = (services: AddedService[]) => {
    if (!services.length) return;

    services.forEach((service) => {
      const isOptional = service.serviceId !== itemId;
      const bindId = isOptional ? itemId : undefined;
      const type = isOptional ? 'optional-service' : 'service';

      tracker.pushAddToCart({
        pageType: 'service',
        item: {
          id: service.serviceId,
          bindId: bindId,
          name: service.name,
          type: type,
          price: service.price.to,
          brand: 'Leroy Merlin',
          categoryTree: 'serviço',
          seller: 'Leroy Merlin',
          quantity: service.quantity,
        },
      });
    });
  };

  const result = useMutation(Services.Checkout.Cart.addServicesToCart, {
    onError,
    onSuccess: (response: AddServicesToCartResponse) => {
      const firstService = response.data[0];

      if (firstService) {
        updateCartItems(firstService.uniqueProductsCount);
      }

      pushAddToCart(response.data);

      onSuccess?.();
    },
  });

  return result;
};

export default useAddServicesToCart;
